import { API_ENDPOINTS } from './apiConfig';

// Generic API request function
const makeApiRequest = async (url, options = {}) => {
  try {
    // Don't set Content-Type for FormData, let browser set it with boundary
    const headers = {};
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }
    
    const response = await fetch(url, {
      headers: {
        ...headers,
        ...options.headers
      },
      ...options
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }
    
    // Handle both JSON and plain text responses like Flutter implementation
    const responseText = await response.text();
    try {
      // Try to parse as JSON first
      const data = JSON.parse(responseText);
      return data;
    } catch (e) {
      // If not JSON, treat as plain text success (like Flutter does)
      console.log('[AuthService] Plain text response:', responseText);
      return { 
        success: true, 
        status: true, 
        message: responseText 
      };
    }
  } catch (error) {
    console.error('API request error:', error);
    throw error;
  }
};

// Generate OTP for mobile number
export const generateOtp = async (mobileNumber) => {
  try {
    // Match Flutter implementation: URL parameters + empty JSON body
    const url = `${API_ENDPOINTS.generateOtp}?mobileNumber=${encodeURIComponent(mobileNumber)}&emailId=&isClient=false`;
    console.log('[AuthService] Sending OTP to:', url);
    
    const response = await makeApiRequest(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({})
    });

    console.log('[AuthService] OTP Response:', response);
    
    // Handle different response formats
    if (response.status === true || response.success === true) {
      return {
        success: true,
        message: response.message || 'OTP sent successfully'
      };
    } else {
      return {
        success: false,
        error: response.message || response.responseData || 'Failed to send OTP'
      };
    }
  } catch (error) {
    console.error('Generate OTP error:', error);
    
    // Return actual error instead of demo mode
    const message = (error?.message && error.message.toLowerCase().includes('failed to fetch'))
      ? 'Unable to send OTP. Please try again.'
      : (error.message || 'Failed to send OTP. Please try again.');
    return {
      success: false,
      error: message
    };
  }
};

// Validate OTP and authenticate user
export const validateOtp = async (mobileNumber, otp) => {
  try {
    // Match Flutter implementation: URL parameters + empty JSON body
    const url = `${API_ENDPOINTS.validateOtp}?mobileNumber=${encodeURIComponent(mobileNumber)}&OTP=${encodeURIComponent(otp)}&emailId=&isClient=false`;
    console.log('[AuthService] Verifying OTP at:', url);
    
    const response = await makeApiRequest(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({})
    });

    console.log('[AuthService] Verify OTP Response:', response);
    
    // Handle different response formats - match Flutter implementation
    // If we get here, HTTP status was 200, so treat as success unless explicitly marked as failure
    if (response.status === true || response.success === true || response.message === 'OTP sent' || !response.hasOwnProperty('status')) {
      console.log('[AuthService] Candidate ID from response:', response.id);
      return {
        success: true,
        data: {
          token: response.token || 'demo-token-' + Date.now(),
          name: response.name || 'User',
          email: response.email || `user${mobileNumber}@channelplay.com`,
          mobile: mobileNumber,
          candidateId: response.id || null
        }
      };
    } else {
      return {
        success: false,
        error: response.responseMessage || response.message || 'Invalid OTP'
      };
    }
  } catch (error) {
    console.error('Validate OTP error:', error);
    
    // Only use demo mode for specific development cases
    if (process.env.NODE_ENV === 'development' && otp === '1234') {
      return {
        success: true,
        data: {
          token: 'demo-token-' + Date.now(),
          name: 'John Doe',
          email: `user${mobileNumber}@channelplay.com`,
          mobile: mobileNumber,
          candidateId: 'demo-candidate-' + Date.now()
        }
      };
    }
    
    const message = (error?.message && error.message.toLowerCase().includes('failed to fetch'))
      ? 'Unable to verify OTP. Please try again.'
      : (error.message || 'Failed to verify OTP. Please try again.');
    return {
      success: false,
      error: message
    };
  }
};

// Get candidate ID from localStorage
export const getCandidateId = () => {
  try {
    // First try to get from dedicated candidateId storage
    const candidateId = localStorage.getItem('candidateId');
    if (candidateId && candidateId !== '') {
      return candidateId;
    }
    
    // Fallback: try to get from user object
    const user = localStorage.getItem('user');
    if (user) {
      const userData = JSON.parse(user);
      return userData.candidateId || null;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting candidate ID:', error);
    return null;
  }
};

// Get user profile
export const getUserProfile = async () => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('No auth token found');
    }

    const response = await makeApiRequest(API_ENDPOINTS.getUserProfile, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (response.success) {
      return {
        success: true,
        data: response.data
      };
    } else {
      return {
        success: false,
        error: response.message || 'Failed to get user profile'
      };
    }
  } catch (error) {
    console.error('Get user profile error:', error);
    
    // Return mock data for demo
    const user = localStorage.getItem('user');
    if (user) {
      return {
        success: true,
        data: JSON.parse(user)
      };
    }
    
    return {
      success: false,
      error: 'Failed to get user profile'
    };
  }
};
